/**
 * Tiny wrapper around `new PerformanceObserver(...).observe(...)` that
 * silently no-ops when the entry type or the API itself is unsupported.
 *
 * Both `bootTiming.js` and `routePerfTracker.js` reach for the same pattern:
 * register an observer, tolerate unsupported entry types in older browsers,
 * and return a disconnect handle. Keeping the "unsupported = no-op" contract
 * in one place prevents drift.
 */

/**
 * @param {string} type — entry type (e.g. 'longtask', 'event', 'paint', 'largest-contentful-paint')
 * @param {PerformanceObserverInit} options — passed through to observe()
 * @param {(entries: PerformanceEntryList) => void} handler
 * @returns {() => void} disconnect function; no-op if observer was never started
 */
export function observePerf(type, options, handler) {
  if (typeof PerformanceObserver === 'undefined') return () => {};
  let observer;
  try {
    observer = new PerformanceObserver((list) => handler(list.getEntries()));
    observer.observe({ type, ...options });
  } catch {
    return () => {};
  }
  return () => {
    try { observer.disconnect(); } catch { /* already disconnected */ }
  };
}
